import React, { useState, useEffect, useRef } from 'react';
import './PhonemeExamplePlayer.css';

interface PhonemeExamplePlayerProps {
  phoneme: string;
  language?: string;
  accent?: string;
}

const PhonemeExamplePlayer: React.FC<PhonemeExamplePlayerProps> = ({ 
  phoneme, 
  language = 'English', 
  accent = 'American' 
}) => {
  const [exampleUrls, setExampleUrls] = useState<string[]>([]);
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Determine backend URL
  const backendUrl = window.location.hostname === 'localhost' 
    ? 'http://localhost:8080'
    : 'https://voice-coach-backend-406125116283.us-east1.run.app';

  useEffect(() => {
    fetchExamples();
    // Cleanup on unmount or when dependencies change
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [phoneme, language, accent]);

  const fetchExamples = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch(
        `${backendUrl}/api/phonemes/approved?language=${language}&accent=${accent}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch phoneme examples');
      }

      const data = await response.json();
      
      // Get URLs for the specific phoneme
      const urls = data[phoneme] || [];
      setExampleUrls(urls);
      setCurrentUrlIndex(0);
      
    } catch (error) {
      console.error('Error fetching phoneme examples:', error);
      setError('Unable to load examples');
    } finally {
      setIsLoading(false);
    }
  };

  const playExample = (index?: number) => {
    if (exampleUrls.length === 0) return;
    
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    // Use provided index or current index
    const playIndex = index !== undefined ? index : currentUrlIndex;
    
    const audio = new Audio(exampleUrls[playIndex]);
    audioRef.current = audio;
    setIsPlaying(true);
    setError(''); // Clear any previous errors
    
    audio.onended = () => {
      setIsPlaying(false);
      audioRef.current = null;
    };
    
    audio.onerror = (e) => {
      console.error('Audio playback error:', e);
      setIsPlaying(false);
      setError(`Failed to play example ${playIndex + 1}`);
      audioRef.current = null;
    };
    
    audio.play().catch(err => {
      console.error('Play failed:', err);
      setIsPlaying(false);
      setError('Failed to play audio');
      audioRef.current = null;
    });
  };

  const selectExample = (index: number) => {
    setCurrentUrlIndex(index);
    if (isPlaying) {
      // If currently playing, play the new selection
      playExample(index);
    }
  };

  if (isLoading) {
    return (
      <div className="phoneme-example-player loading">
        <span className="loading-text">Loading examples...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="phoneme-example-player error">
        <span className="error-text">{error}</span>
      </div>
    );
  }

  if (exampleUrls.length === 0) {
    return (
      <div className="phoneme-example-player no-examples">
        <span className="no-examples-text">No examples available</span>
      </div>
    );
  }

  return (
    <div className="phoneme-example-player">
      <button 
        className={`example-play-btn ${isPlaying ? 'playing' : ''}`}
        onClick={() => playExample()}
        title={`Play example ${currentUrlIndex + 1} of ${exampleUrls.length}`}
      >
        {isPlaying ? '🔊' : '🎧'} 
        <span className="btn-text">
          {isPlaying ? 'Playing...' : 'Hear Example'}
        </span>
      </button>
      
      {exampleUrls.length > 1 && (
        <div className="example-navigation">
          <button 
            className="nav-btn prev"
            onClick={() => {
              const newIndex = (currentUrlIndex - 1 + exampleUrls.length) % exampleUrls.length;
              selectExample(newIndex);
            }}
            title="Previous example"
          >
            ‹
          </button>
          
          <span className="example-count">
            {currentUrlIndex + 1}/{exampleUrls.length}
          </span>
          
          <button 
            className="nav-btn next"
            onClick={() => {
              const newIndex = (currentUrlIndex + 1) % exampleUrls.length;
              selectExample(newIndex);
            }}
            title="Next example"
          >
            ›
          </button>
        </div>
      )}
      
      {exampleUrls.length > 1 && (
        <div className="example-dots">
          {exampleUrls.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentUrlIndex ? 'active' : ''}`}
              onClick={() => selectExample(index)}
              title={`Play example ${index + 1}`}
              aria-label={`Play example ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PhonemeExamplePlayer;